<template>
  <div v-if='isBrandProtectionEnabled'>
    <div class="infostealers-tab"  v-if='!dataLoading && !infostealersLoading'>
      <div v-if="!hasKeywords" class="infostealers-empty">
        <div class="infostealers-empty__card">
          <div class="infostealers-empty__badge">
            <v-icon size="34" color="primary">mdi-shield-search</v-icon>
          </div>
          <h3 class="infostealers-empty__title">{{ emptyStateMessage.title }}</h3>
          <p class="infostealers-empty__text">{{ emptyStateMessage.text }}</p>
          <v-btn color="primary" depressed rounded class="infostealers-empty__cta" @click="goToInfostealersSettings">
            <v-icon small left>mdi-plus</v-icon>
            Add keyword in settings
          </v-btn>
        </div>
      </div>
      <div class="infostealers-tab__header" v-if="hasKeywords">
        <div class="filters-container">
          <div class="filter-item">
            <label>Stealer Type</label>
            <v-autocomplete
              v-model="filters.type"
              :items="stealerTypes"
              outlined
              dense
              placeholder="All Types"
              hide-details
            />
          </div>
          <div class="filter-item">
            <label>Username</label>
            <v-autocomplete
              v-model="filters.username"
              :items="availableUsernames"
              outlined
              dense
              placeholder="Filter by username"
              hide-details
            />
          </div>
          <div class="filter-item">
            <label>Hostname</label>
            <v-autocomplete
              v-model="filters.hostname"
              :items="availableHostnames"
              outlined
              dense
              placeholder="Filter by hostname"
              hide-details
            />
          </div>
          <div class="filter-item">
            <label>IP Address</label>
            <v-autocomplete
              v-model="filters.ip"
              :items="availableIPs"
              outlined
              dense
              placeholder="Filter by IP"
              hide-details
            />
          </div>
          <div class="filter-item">
            <label>Country</label>
            <v-autocomplete
              v-model="filters.country"
              :items="availableCountries"
              outlined
              dense
              placeholder="Filter by country"
              hide-details
            />
          </div>
          <div class="filter-item">
            <label>Keyword</label>
            <v-autocomplete
              v-model="filters.keyword"
              :items="availableKeywords"
              outlined
              dense
              placeholder="Filter by keyword"
              hide-details
            />
          </div>
          <div class="filter-item">
            <label>Operating System</label>
            <v-autocomplete
              v-model="filters.os"
              :items="availableOS"
              outlined
              dense
              placeholder="Filter by OS"
              hide-details
            />
          </div>
          <div class="header-actions">
            <v-btn text small class="export-btn" @click="exportCSV">
              <v-icon small left>mdi-download</v-icon>
              CSV
            </v-btn>
            <v-btn text small class="export-btn" @click="exportJSON">
              <v-icon small left>mdi-download</v-icon>
              JSON
            </v-btn>
            <v-btn text small class="export-btn" @click="showVisualizations">
              <v-icon small left>mdi-chart-bar</v-icon>
              STATISTICS
            </v-btn>
          </div>
        </div>
      </div>

      <div class="infostealers-content" v-if="hasKeywords">
        <div class="content-wrapper">
          <!-- Left side: Table -->
          <div class="table-section">
            <div class="results-count">
              {{ resultsRangeText }}
            </div>
            <v-data-table
              ref="mainDataTable"
              :key='mainTableKey'
              item-key='uuid'
              :headers="headers"
              :items="formattedItems"
              :items-per-page="itemsPerPage"
              class="elevation-1"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              @update:sort-by="handleSort('column', $event)"
              @update:sort-desc="handleSort('order', $event)"
              @click:row="showEndpointDetails"
              :footer-props="footerProps"
              :item-class="rowClasses"
              :page="currentPage"
              :server-items-length="totalItems"
              :options="getMainTableOptions"
              @update:page="handlePageChange"
              @update:items-per-page="updateItemsPerPage"
              :disable-pagination="false"
              :hide-default-footer="false"
              :page-count="totalPages"
              :disable-next-page="!hasNext"
              :disable-prev-page="!hasPrevious"
            >
              <!-- Custom column formatting -->
              <template v-slot:item.timestamp="{ item }">
                <span>
                  {{ formatDate(item.timestamp, item.displayTimestamp) }}
                </span>
              </template>

              <template v-slot:item.type="{ item }">
                <v-chip
                  :color="getTypeColor(item.type)"
                  text-color="white"
                  small
                  class="clickable-cell"
                  @click.stop="showInfostealerDetails(item.type)"
                >
                  {{ item.type }}
                </v-chip>
              </template>

              <template v-slot:item.username="{ item }">
                <span @click.stop="copyToClipboard(item.username)" class="clickable-cell">
                  {{ item.username }}
                </span>
              </template>

              <template v-slot:item.hostname="{ item }">
                <span @click.stop="copyToClipboard(item.hostname)" class="clickable-cell">
                  {{ item.hostname }}
                </span>
              </template>

              <template v-slot:item.ipAddress="{ item }">
                <span @click.stop="copyToClipboard(item.ipAddress)" class="clickable-cell">
                  {{ item.ipAddress }}
                </span>
              </template>

              <template v-slot:item.country="{ item }">
                <span @click.stop="copyToClipboard(item.country)" class="clickable-cell">
                  {{ item.country }}
                </span>
              </template>

              <template v-slot:item.keyword="{ item }">
                <span @click.stop="copyToClipboard(item.keyword)" class="clickable-cell">
                  {{ item.keyword }}
                </span>
              </template>

              <template v-slot:item.severity="{ item }">
                <span
                  :style="{ color: getSeverityColor(item.displayTimestamp) }"
                >
                  {{ getSeverityScore((item.displayTimestamp)).toFixed(1) }}
                </span>
              </template>

              <template v-slot:item.compromisedData="{ item }">
                <div class="compromised-data">
                  <v-chip
                    small
                    :class="['mr-2', 'compromised-chip', item.credentials > 0 ? 'chip-credentials' : 'chip-zero']"
                  >
                    {{ item.credentials }} Credentials
                  </v-chip>
                  <v-chip
                    small
                    :class="['compromised-chip', item.cookies.length > 0 ? 'chip-cookies' : 'chip-zero']"
                  >
                    {{ item.cookies.length }} Cookies
                  </v-chip>
                </div>
              </template>
            </v-data-table>
          </div>

          <!-- Right side: Endpoint Details -->
          <div v-if="selectedEndpoint" class="details-section">
            <div class="details-header">
              <h3>Infection Details</h3>
              <button class="close-button" @click="closeDetails">×</button>
            </div>

            <div class="details-content">
              <div class="basic-info">
                <h4>Basic Information</h4>
                <div class="info-row">
                  <div class="info-label">Type</div>
                  <div class="info-value">
                    <v-chip
                      :color="getTypeColor(selectedEndpoint.type)"
                      text-color="white"
                      small
                      class="clickable-cell"
                      @click.stop="showInfostealerDetails(selectedEndpoint.type)"
                    >
                      {{ selectedEndpoint.type }}
                    </v-chip>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">Severity</div>
                  <div class="info-value">
                    <v-chip
                      :color="getSeverityColor(selectedEndpoint.displayTimestamp)"
                      text-color="white"
                      small
                    >
                      {{ getSeverityScore((selectedEndpoint.displayTimestamp)).toFixed(1) }}
                    </v-chip>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">Keyword</div>
                  <div class="info-value">{{ selectedEndpoint.keyword || '-' }}</div>
                </div>
              </div>

              <div class="system-info">
                <h4>System Information</h4>
                <div class="info-grid">
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-desktop-classic</v-icon>
                    <div>
                      <div class="info-label">Hostname</div>
                      <div class="info-value">{{ formattedHostname }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-ip-network</v-icon>
                    <div>
                      <div class="info-label">IP Address</div>
                      <div class="info-value">{{ formattedIP }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-map-marker</v-icon>
                    <div>
                      <div class="info-label">Location</div>
                      <div class="info-value">{{ formattedLocation }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-microsoft-windows</v-icon>
                    <div>
                      <div class="info-label">Operating System</div>
                      <div class="info-value">{{ formattedOS }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-calendar</v-icon>
                    <div>
                      <div class="info-label">Installation Date</div>
                      <div class="info-value">{{ formattedInstallation }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-clock-outline</v-icon>
                    <div>
                      <div class="info-label">Timezone</div>
                      <div class="info-value">{{ formattedTimezone }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-memory</v-icon>
                    <div>
                      <div class="info-label">RAM</div>
                      <div class="info-value">{{ formattedRamSize }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-cpu-64-bit</v-icon>
                    <div>
                      <div class="info-label">CPU</div>
                      <div class="info-value">{{ formattedCPU }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-gpu</v-icon>
                    <div>
                      <div class="info-label">GPU</div>
                      <div class="info-value">{{ formattedGPU }}</div>
                    </div>
                  </div>
                  <!-- New fields -->
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-fingerprint</v-icon>
                    <div>
                      <div class="info-label">HWID</div>
                      <div class="info-value">{{ formattedHWID }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-source-branch</v-icon>
                    <div>
                      <div class="info-label">Source Name</div>
                      <div class="info-value">{{ formattedSourceName }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-lan</v-icon>
                    <div>
                      <div class="info-label">NetBIOS</div>
                      <div class="info-value">{{ formattedNetBIOS }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-package-variant</v-icon>
                    <div>
                      <div class="info-label">Build</div>
                      <div class="info-value">{{ formattedBuild }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-identifier</v-icon>
                    <div>
                      <div class="info-label">LID</div>
                      <div class="info-value">{{ formattedLID }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-monitor</v-icon>
                    <div>
                      <div class="info-label">Display Resolution</div>
                      <div class="info-value">{{ formattedResolution }}</div>
                    </div>
                  </div>
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-laptop</v-icon>
                    <div>
                      <div class="info-label">Device Type</div>
                      <div class="info-value">{{ formattedLaptop }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="security-status">
                <h4>Endpoint Security</h4>
                <div class="status-row">
                  <v-icon :color="hasAntivirus ? 'success' : 'error'" small>
                    {{ hasAntivirus ? 'mdi-shield-check' : 'mdi-shield-off' }}
                  </v-icon>
                  <span>
                    {{ antivirusName }}
                  </span>
                </div>
                <div class="info-grid">
                  <div class="info-item">
                    <v-icon small class="mr-2">mdi-file-path</v-icon>
                    <div>
                      <div class="info-label">Execution Path</div>
                      <div class="info-value">{{ executionPath }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="compromised-data-section">
                <h4>Compromised Data</h4>
                <div class="data-grid">
                  <div class="data-item credentials" @click="viewRawLog" style="cursor: pointer;" :style="selectedEndpoint.credentials > 0 ? { backgroundColor: '#E132A020', color: '#E132A0' } : {}">
                    <div class="count" :style="selectedEndpoint.credentials > 0 ? { color: '#E132A0', fontWeight: 'bold' } : {}">{{ selectedEndpoint.credentials }}</div>
                    <div class="label">Credentials</div>
                  </div>
                  <div class="data-item cookies"
                       @click="viewCookiesLog"
                       style="cursor: pointer;" :style="selectedEndpoint.cookies.length > 0 ? { backgroundColor: '#FBA21220', color: '#FBA212' } : {}">
                    <div class="count" :style="selectedEndpoint.cookies.length > 0 ? { color: '#FBA212', fontWeight: 'bold' } : {}">{{ selectedEndpoint.cookies.length }}</div>
                    <div class="label">Cookies</div>
                  </div>
                  <div class="data-item cards">
                    <div class="count">0</div>
                    <div class="label">Cards</div>
                  </div>
                </div>
              </div>
              <div class="details-actions">
                <v-btn text small @click="viewRawLog">
                  <v-icon small left>mdi-file-document-outline</v-icon>
                  View Raw Log
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Raw Log Dialog -->
      <v-dialog v-model="showRawLogDialog" max-width="1250"  @keydown.esc="closeRawLog"
                @click:outside='closeRawLog'>
        <v-card class="raw-log-card">
          <v-card-title class="raw-log-card__title d-flex align-center">
            <span>Raw Log Data</span>
            <v-spacer></v-spacer>
            <div class="export-actions mr-4">
              <v-btn
                small
                text
                class="mr-2"
                @click="exportCurrentTabCSV"
                :disabled="!hasDataToExport"
              >
                <v-icon small left>mdi-file-delimited</v-icon>
                CSV
              </v-btn>
              <v-btn
                small
                text
                @click="exportCurrentTabJSON"
                :disabled="!hasDataToExport"
              >
                <v-icon small left>mdi-code-json</v-icon>
                JSON
              </v-btn>
            </div>
            <v-btn icon @click="closeRawLog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="raw-log-dialog-content-header">
            <filtered-search
              ref="rawLogSearch"
              @searchInput="searchChange"
              :show-filters="false"
              class="raw-log-dialog-search"
              :searchItem="searchTerm"
            />
            <v-switch
              v-model="hasKeyword"
              label="Has Keyword"
              class="has-keyword-toggle"
            />
          </div>
          <v-card-text>
            <v-tabs v-model="activeTab" background-color="transparent">
              <v-tab>
                <v-icon left>mdi-key</v-icon>
                Passwords
              </v-tab>
              <v-tab>
                <v-icon left>mdi-cookie</v-icon>
                Cookies
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <v-tab-item>
                <v-data-table
                  :headers="passwordHeaders"
                  :items="passwordItems"
                  :items-per-page="20"
                  class="raw-log-table mt-4"
                  :footer-props="{
                    'items-per-page-options': [20, 30, 50],
                    'items-per-page-text': 'Rows per page:',
                    'items-per-page-all-text': 'All'
                  }"
                >
                  <template v-slot:item.SOFT="{ item }">
                    <span @click.stop="copyToClipboard(item.SOFT)" class="clickable-cell">
                      {{ item.SOFT }}
                    </span>
                  </template>
                  <template v-slot:item.URL="{ item }">
                    <span @click.stop="copyToClipboard(item.URL)" class="clickable-cell">
                      {{ item.URL }}
                    </span>
                  </template>
                  <template v-slot:item.USER="{ item }">
                    <span @click.stop="copyToClipboard(item.USER)" class="clickable-cell">
                      {{ item.USER }}
                    </span>
                  </template>
                  <template v-slot:item.PASS="{ item }">
                    <span @click.stop="copyToClipboard(item.PASS)" class="clickable-cell">
                      {{ item.PASS }}
                    </span>
                  </template>
                  <template v-slot:no-data>
                    <div class="text-center pa-4">No password data available</div>
                  </template>
                </v-data-table>
              </v-tab-item>

              <v-tab-item>
                <v-data-table
                  :headers="cookieHeaders"
                  :items="cookieItems"
                  :items-per-page="20"
                  class="raw-log-table mt-4"
                  :footer-props="{
                    'items-per-page-options': [20, 30, 50],
                    'items-per-page-text': 'Rows per page:',
                    'items-per-page-all-text': 'All'
                  }"
                >
                  <template v-slot:item.application="{ item }">
                    <span @click.stop="copyToClipboard(item.application)" class="clickable-cell">
                      {{ item.application }}
                    </span>
                  </template>
                  <template v-slot:item.url="{ item }">
                    <span @click.stop="copyToClipboard(item.url)" class="clickable-cell">
                      {{ item.url }}
                    </span>
                  </template>
                  <template v-slot:item.username="{ item }">
                    <span @click.stop="copyToClipboard(item.username)" class="clickable-cell">
                      {{ item.username }}
                    </span>
                  </template>
                  <template v-slot:item.password="{ item }">
                    <span @click.stop="copyToClipboard(item.password)" class="clickable-cell">
                      {{ item.password }}
                    </span>
                  </template>
                  <template v-slot:item.secure="{ item }">
                    <span @click.stop="copyToClipboard(item.secure)" class="clickable-cell">
                      {{ item.secure }}
                    </span>
                  </template>
                  <template v-slot:item.expiry="{ item }">
                    <span @click.stop="copyToClipboard(item.expiry)" class="clickable-cell">
                      {{ item.expiry }}
                    </span>
                  </template>
                  <template v-slot:no-data>
                    <div class="text-center pa-4">No cookie data available</div>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Infostealer Details Dialog -->
      <v-dialog v-model="showInfostealerDialog" max-width="800">
        <v-card v-if="selectedInfostealer">
          <v-card-title class="headline d-flex align-center">
            <span>{{ selectedInfostealer }} Details</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeInfostealerDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <div v-if="infostealerDetails[selectedInfostealer]">
              <div class="section mb-6">
                <h3 class="section-title mb-2">Technical Details</h3>
                <div class="technical-details-grid">
                  <div v-for="(value, key) in infostealerDetails[selectedInfostealer]['Technical Details']" :key="key" class="technical-detail-item">
                    <div class="detail-label">{{ key }}</div>
                    <div class="detail-value">{{ value }}</div>
                  </div>
                </div>
              </div>

              <div class="section mb-6">
                <h3 class="section-title mb-2">Delivery Mechanism</h3>
                <p class="section-content">{{ infostealerDetails[selectedInfostealer]['Delivery Mechanism'] }}</p>
              </div>

              <div class="section mb-6">
                <h3 class="section-title mb-2">Infection Lifecycle</h3>
                <ul class="lifecycle-list">
                  <li v-for="(step, index) in parseNumberedList(infostealerDetails[selectedInfostealer]['Infection Lifecycle'])"
                      :key="index"
                      class="lifecycle-item">
                    {{ step }}
                  </li>
                </ul>
              </div>

              <div class="section mb-6">
                <h3 class="section-title mb-2">Indicators of Compromise (IoCs)</h3>
                <ul class="ioc-list">
                  <li v-for="(ioc, index) in infostealerDetails[selectedInfostealer]['Indicators of Compromise (IoCs)']" :key="index" class="ioc-item">
                    {{ ioc }}
                  </li>
                </ul>
              </div>

              <div class="section mb-6">
                <h3 class="section-title mb-2">Notable Campaigns / Threat Actor Attribution</h3>
                <p class="section-content">{{ infostealerDetails[selectedInfostealer]['Notable Campaigns / Threat Actor Attribution'] }}</p>
              </div>

              <div class="section mb-6">
                <h3 class="section-title mb-2">Hacker's Perspective</h3>
                <p class="section-content">{{ infostealerDetails[selectedInfostealer]["Hacker's Perspective"] }}</p>
              </div>
              <div class="section">
                <h3 class="section-title mb-2">Detection & Forensics Recommendations</h3>
                <p class="section-content">{{ infostealerDetails[selectedInfostealer]['Detection & Forensics Recommendations'] }}</p>
              </div>
            </div>
            <div v-else class="text-center pa-4">
              No detailed information available for this infostealer.
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Add snackbar at the end of the template -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
        :style="{
          position: 'fixed',
          top: '20px',
          right: '20px',
          maxWidth: '300px'
        }"
      >
        <div class="d-flex align-center">
          <v-icon small class="mr-2">{{ snackbar.icon }}</v-icon>
          {{ snackbar.text }}
        </div>
      </v-snackbar>

      <!-- Visualizations Dialog -->
      <v-dialog
        v-model="showVisualizationDialog"
        max-width="1200"
        scrollable
      >
        <v-card>
          <v-card-title class="headline d-flex align-center">
            <span>STATISTICS VIEWER</span>
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              class="mr-2"
              @click="exportStatisticsHTML"
              :disabled="!showVisualizationDialog"
            >
              <v-icon small left>mdi-file-code</v-icon>
              EXPORT HTML
            </v-btn>
            <v-btn icon class="close-visualizations-btn" @click="closeVisualizations">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-container v-if="showVisualizationDialog" fluid class="visualization-dialog pa-0">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="chart-container">
                    <h3 class="chart-title">INFOSTEALER DISTRIBUTION</h3>
                    <div ref="pieChart" class="chart"></div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="chart-container">
                    <h3 class="chart-title">TOP AFFECTED COUNTRIES</h3>
                    <div ref="barChart" class="chart"></div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="chart-container">
                    <h3 class="chart-title">TOP INFECTED OPERATING SYSTEMS</h3>
                    <div ref="osChart" class="chart"></div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="chart-container">
                    <h3 class="chart-title">KEYWORD DISTRIBUTION</h3>
                    <div ref="keywordChart" class="chart"></div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="chart-container">
                    <h3 class="chart-title">INFECTIONS TIMELINE</h3>
                    <div ref="lineChart" class="chart"></div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="chart-container">
                    <h3 class="chart-title">COMPROMISED DATA TYPES</h3>
                    <div ref="stackedChart" class="chart"></div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
    <div class='infostealers-tab-loading' v-else />
    <page-loading-overlay
      :show="infostealersLoading && currentTab === 'infostealers'"
      :progress="infostealersProgress"
      contained
    />
  </div>
  <div v-else class='license-banner'>
    <feature-toggle-disabled
      :title='"brand.protection.disabled.infostealers.header"'
      :message='"cloud.services.disabled.popup.message"'
    />
  </div>
</template>

<script>
import * as d3 from 'd3';
import { v4 as uuidv4 } from 'uuid';
import crypto from 'crypto';
import * as dateParser from 'any-date-parser';
import infostealerDetails from '@/assets/infostealers/infostealerDetails.json';
import { mapGetters } from 'vuex';
import {
  CHANGE_INFOSTEALERS_LIST_OPTIONS,
  CLEAR_INFOSTEALERS_ITEMS,
  CLEAR_INFOSTEALERS_CACHE,
  CLEAR_INFOSTEALERS_BACKEND_CACHE,
} from '@/store/configuration/actionTypes';
import { SET_INFOSTEALERS_FILTERS, SET_INFOSTEALERS_LOADING } from '@/store/configuration/mutationTypes';
import { views } from '@/router/constants';
import { GET_SYSTEM_SETTINGS } from '@/store/user/actionTypes';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import FeatureToggleDisabled from '@/components/common/FeatureToggleDisabled.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import { CLEAR_INFOSTEALERS_PAGINATION_STATE } from '@/store/pagination/actionTypes';
import { configuredKeywords } from '@/utils/infostealersProgress';

export default {
  name: 'InfostealersTab',
  components: { FeatureToggleDisabled, PageLoadingOverlay, FilteredSearch },
  props: {
    currentTab: {
      required: false,
    },
  },
  data() {
    return {
      // Tracks the scanning->done edge so we refetch once when the scan hits 100%.
      scanWasActive: false,
      filters: {
        type: 'All Types',
        country: '',
        os: '',
        username: '',
        hostname: '',
        ip: '',
        keyword: '',
      },
      headers: [
        {
          text: 'Timestamp',
          value: 'timestamp',
          align: 'left',
          width: '12%',
        },
        {
          text: 'Type',
          value: 'type',
          align: 'left',
          width: '10%',
        },
        {
          text: 'Username',
          value: 'username',
          align: 'left',
          width: '10%',
        },
        {
          text: 'Hostname',
          value: 'hostname',
          align: 'left',
          width: '15%',
        },
        {
          text: 'IP Address',
          value: 'ipAddress',
          align: 'left',
          width: '12%',
        },
        {
          text: 'Country',
          value: 'country',
          align: 'left',
          width: '7%',
        },
        {
          text: 'Keyword',
          value: 'keyword',
          align: 'left',
          width: '12%',
        },
        {
          text: 'Severity',
          value: 'severity',
          align: 'left',
          width: '6%',
          sortable: false,
        },
        {
          text: 'Compromised Data',
          value: 'compromisedData',
          align: 'left',
          width: '17%',
          sortable: false,
        },
      ],
      selectedEndpoint: null,
      showRawLogDialog: false,
      activeTab: 0,
      passwordHeaders: [
        { text: 'Software', value: 'SOFT', width: '20%' },
        { text: 'URL', value: 'URL', width: '40%' },
        { text: 'Username', value: 'USER', width: '20%' },
        { text: 'Password', value: 'PASS', width: '20%' },
      ],
      cookieHeaders: [
        { text: 'Application', value: 'application', width: '15%' },
        { text: 'URL', value: 'url', width: '30%' },
        { text: 'Key', value: 'username', width: '15%' },
        { text: 'Value', value: 'password', width: '20%' },
        { text: 'Secure', value: 'secure', width: '10%' },
        { text: 'Expiry', value: 'expiry', width: '10%' },
      ],
      showCopyTooltip: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 1000,
        icon: 'mdi-content-copy',
      },
      showInfostealerDialog: false,
      selectedInfostealer: null,
      infostealerDetails,
      sortInfostealers: {},
      sortBy: ['timestamp'],
      sortDesc: [true],
      showVisualizationDialog: false,
      search: '',
      filterSearch: '',
      hasExploit: false,
      dataVersion: 0,
      polling: null,
      searchTerm: '',
      hasKeyword: false,
    };
  },
  beforeDestroy() {
    this.$store.dispatch(CLEAR_INFOSTEALERS_PAGINATION_STATE);
    this.$store.dispatch(CLEAR_INFOSTEALERS_ITEMS);
    this.$store.dispatch(CLEAR_INFOSTEALERS_CACHE);
    this.$store.dispatch(CLEAR_INFOSTEALERS_BACKEND_CACHE);
    this.selectedEndpoint = null;
    if (this.polling) {
      clearInterval(this.polling);
      this.polling = null;
    }
  },
  async mounted() {
    // Read-only load (never a write) so entering the tab does not re-push
    // keywords. The results load (loadInfostealers) triggers the scan for the
    // tenant's own resolved keywords server-side.
    await this.$store.dispatch(GET_SYSTEM_SETTINGS);
    this.preselectFirstKeyword();
    this.loadInfostealers(-1, 1);
  },
  computed: {
    ...mapGetters([
      'infostealersItems',
      'infostealersLoading',
      'dataLoading',
      'infostealersPaginationState',
      'infostealersFilters',
      'systemSettings',
      'hasLicenseModule',
      'infostealersRefId',
      'infostealersProgress',
      'infostealersProgressData',
      'infostealersRunningInterval',
      'organizationFilter',
      'domainFilter',
    ]),
    getMainTableOptions() {
      return {
        page: this.currentPage,
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      };
    },
    mainTableKey() {
      return `infostealers-main-table-${this.dataVersion}`;
    },
    isBrandProtectionEnabled() {
      return this.hasLicenseModule('infostealers');
    },
    hasKeywords() {
      // Whether any keyword is in scope (org + domain). When none, the tab must
      // not call the results endpoint (it would 400 on empty keywords and hang).
      return this.availableKeywords.length > 0;
    },
    emptyStateMessage() {
      // Context-aware CTA: if the user is filtered on specific domain(s) that
      // have no configured keyword, name them so it's clear that domain isn't
      // monitored. Otherwise a generic "no keywords configured" message.
      const domains = this.domainFilter || [];
      if (domains.length > 0) {
        // Cap the list so a large multi-select doesn't render dozens of names.
        const shown = domains.slice(0, 3);
        const extra = domains.length - shown.length;
        const label = extra > 0 ? `${shown.join(', ')} +${extra} more` : shown.join(', ');
        return {
          title: `No infostealer keyword for ${label}`,
          text: `There's no keyword in Brand Protection for ${label}, so it isn't being monitored. Add it on the Infostealers page in Tenant Settings to start a scan.`,
        };
      }
      return {
        title: 'No infostealer keywords configured',
        text: 'Add a keyword on the Infostealers page in Tenant Settings to initiate a scan.',
      };
    },
    currentPage() {
      return this.infostealersPaginationState?.page || 1;
    },
    itemsPerPage() {
      return this.infostealersPaginationState?.itemsPerPage || 20;
    },
    totalItems() {
      return (this.infostealersPaginationState && this.infostealersPaginationState.total) || 0;
    },
    stealerTypes() {
      return this.infostealersFilters.infostealersList;
    },
    availableCountries() {
      return this.infostealersFilters.countryList;
    },
    availableOS() {
      return this.infostealersFilters.osList;
    },
    availableIPs() {
      return this.infostealersFilters.ipList;
    },
    availableUsernames() {
      return this.infostealersFilters.usernamesList;
    },
    availableHostnames() {
      return this.infostealersFilters.hostnamesList;
    },
    availableKeywords() {
      // Show all keywords for the selected org(s), not just the filtered response.
      // Only platform + legacy keywords are ever shown (never api / cyn-ai).
      const bp = this.systemSettings?.settingsMetaData?.brandProtection || {};
      const platformEntries = bp.keywordsBySource?.platform || [];
      const legacyKeywords = bp.keywordsList || [];
      const selectedOrgs = this.organizationFilter || [];

      const all = [...new Set([
        ...platformEntries.map(e => (typeof e === 'string' ? e : e.keyword)),
        ...legacyKeywords,
      ])];

      let keywords = all;
      if (selectedOrgs.length > 0 && platformEntries.length > 0) {
        const orgs = this.$store.getters.organizations || [];
        const selectedOrgIds = new Set(
          orgs.filter(o => selectedOrgs.includes(o.name)).map(o => o.id),
        );
        const orgKeywords = platformEntries
          .filter(e => typeof e !== 'string' && selectedOrgIds.has(e.orgId))
          .map(e => e.keyword);
        // Fall back to all when the org has no platform keywords (preserves prior behavior)
        keywords = orgKeywords.length > 0 ? orgKeywords : all;
      }

      // Scope by the selected domain(s): in this tab a keyword IS a domain, so
      // only keywords matching the selected domain(s) are shown. A selected
      // domain that is not a configured keyword yields an empty list, which in
      // turn shows no results and triggers no scan.
      const selectedDomains = this.domainFilter || [];
      if (selectedDomains.length > 0) {
        const domainSet = new Set(selectedDomains);
        keywords = keywords.filter(k => domainSet.has(k));
      }

      return [...new Set(keywords)];
    },
    hasAntivirus() {
      return (
        this.selectedEndpoint &&
        this.selectedEndpoint.system &&
        this.selectedEndpoint.system['Anti Virus'] &&
        this.selectedEndpoint.system['Anti Virus'].toLowerCase() !== 'none'
      );
    },
    antivirusName() {
      if (!this.selectedEndpoint?.system) return '-';
      const av = this.selectedEndpoint.system['Anti Virus'];
      if (!av || av.toLowerCase() === 'none') return '-';
      return av;
    },
    executionPath() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system.Path ||
             this.selectedEndpoint.system.FileLocation ||
             '-';
    },
    formattedRamSize() {
      if (!this.selectedEndpoint?.system) return '-';

      const ramSize = this.selectedEndpoint.system['RAM Size'] ||
                     this.selectedEndpoint.system.RAM;

      if (!ramSize) return '-';

      const ramInMB = parseInt(ramSize, 10);
      if (Number.isNaN(ramInMB)) {
        return '-';
      }
      const ramInGB = (ramInMB / 1024).toFixed(1);
      return `${ramInGB} GB`;
    },
    severityScore() {
      if (!this.selectedEndpoint?.timestamp) return { score: 0, color: '#9e9e9e' };
      const timestamp = new Date(this.selectedEndpoint.timestamp);
      const now = new Date();
      const diffMonths = (now.getFullYear() - timestamp.getFullYear()) * 12 + (now.getMonth() - timestamp.getMonth());
      if (diffMonths < 3) {
        return { score: 10.0, color: '#e0255e' }; // red
      }
      if (diffMonths < 6) {
        return { score: 7.5, color: '#ff5722' }; // deep-orange
      }
      if (diffMonths < 12) {
        return { score: 5.0, color: '#ffb74d' }; // light-orange
      }
      return { score: 2.5, color: '#80cbc4' }; // light teal
    },
    formattedItems() {
      let ids = [];
      const items = Object.freeze(this.infostealersItems.map(endpoint => ({
        timestamp: endpoint.system['Install Date'] || endpoint.system['Installation Date'] || endpoint.system.Date ||
            endpoint.system['Log date'] || endpoint.system.Time || endpoint.system['Local Time'] || endpoint.system['Collection Time'] || 'N/A',
        displayTimestamp: endpoint.system.displayTimestamp || null,
        type: endpoint.system['Infostealer Name'] || 'Unknown',
        keyword: endpoint.keyword || '-',
        username: endpoint.system.User || endpoint.system.UserName || 'N/A',
        hostname: endpoint.system.Computer || endpoint.system['Computer Name'] || endpoint.system.Hostname || endpoint.system.NetBIOS || endpoint.system.MachineName || 'N/A',
        ipAddress: endpoint.system['IP Address'] || 'N/A',
        country: endpoint.system.Country || 'N/A',
        severity: 'N/A',
        credentials: endpoint.passwords?.length || 0,
        id: endpoint.id,
        system: endpoint.system,
        passwords: endpoint.passwords || [],
        cookies: endpoint.cookies || [],
        uuid: (() => {
          let generatedId = this.generateStableId(endpoint);
          if (ids.includes(generatedId)) {
            generatedId = `${generatedId}-${endpoint.id}`;
          } else {
            ids.push(generatedId);
          }
          return generatedId;
        })(),
      })));
      ids = null;
      return items;
    },
    formattedCPU() {
      if (!this.selectedEndpoint?.system) return '-';
      // Try different possible CPU field names
      return this.selectedEndpoint.system['CPU Name'] ||
             this.selectedEndpoint.system.CPU ||
             '-';
    },
    formattedInstallation() {
      if (!this.selectedEndpoint?.system) return '-';
      const date = this.selectedEndpoint.system['Install Date'] ||
                   this.selectedEndpoint.system['Installation Date'] ||
                   this.selectedEndpoint.system['Log date'] ||
                   this.selectedEndpoint.system.Time ||
                   this.selectedEndpoint.system['Local Time'];
      return date ? date.replace(/\//g, '.') : '-';
    },
    formattedLocation() {
      if (!this.selectedEndpoint?.system?.Country || this.selectedEndpoint.system.Country === 'N/A') return '-';
      return this.selectedEndpoint.system.Country;
    },
    formattedHostname() {
      if (!this.selectedEndpoint?.system) return '-';
      const hostname = this.selectedEndpoint.system.MachineName ||
                      this.selectedEndpoint.system.Computer ||
                      this.selectedEndpoint.system.Hostname ||
                      this.selectedEndpoint.system.NetBIOS ||
                      this.selectedEndpoint.system['Computer Name'];
      return hostname || '-';
    },
    formattedTimezone() {
      if (!this.selectedEndpoint?.system) return '-';
      // First try explicit timezone
      const timeZone = this.selectedEndpoint.system['Time Zone'];
      if (timeZone && timeZone !== 'N/A') return timeZone;
      // Then try UTC offset
      const utcOffset = this.selectedEndpoint.system.UTC;
      if (utcOffset !== undefined) {
        const sign = parseInt(utcOffset, 10) >= 0 ? '+' : '';
        return `UTC${sign}${utcOffset}`;
      }
      return '-';
    },
    formattedOS() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system['Operation System'] ||
             this.selectedEndpoint.system['OS Version'] ||
             this.selectedEndpoint.system.OS ||
             '-';
    },
    formattedIP() {
      if (!this.selectedEndpoint?.system?.['IP Address'] || this.selectedEndpoint.system['IP Address'] === 'N/A') return '-';
      return this.selectedEndpoint.system['IP Address'];
    },
    resultsRangeText() {
      const total = this.totalItems;
      if (total === 0) return 'No results';
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, total);
      return `${start}-${end} / ${total} results`;
    },
    hasDataToExport() {
      return this.activeTab === 0 ? this.passwordItems.length > 0 : this.cookieItems.length > 0;
    },
    formattedHWID() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system.HWID || '-';
    },
    formattedSourceName() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system['Source Name'] || '-';
    },
    formattedNetBIOS() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system.NetBIOS || '-';
    },
    formattedBuild() {
      if (!this.selectedEndpoint?.system) return '-';
      // Look for any key ending with 'Build' in the system object
      const buildKey = Object.keys(this.selectedEndpoint.system)
        .find(key => key.endsWith('Build'));
      return buildKey ? this.selectedEndpoint.system[buildKey] : '-';
    },
    formattedLID() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system.LID || '-';
    },
    formattedResolution() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system.Resolution ||
             this.selectedEndpoint.system['Display Resolution'] || '-';
    },
    formattedLaptop() {
      if (!this.selectedEndpoint?.system) return '-';
      const isLaptop = this.selectedEndpoint.system.Laptop;
      if (isLaptop === undefined || isLaptop === null) return '-';
      return isLaptop === true ? 'Laptop' : 'PC';
    },
    formattedGPU() {
      if (!this.selectedEndpoint?.system) return '-';
      return this.selectedEndpoint.system.GPU ||
             this.selectedEndpoint.system['Graphics Card'] ||
             this.selectedEndpoint.system['Video Card'] ||
             '-';
    },
    hasNext() {
      return (this.infostealersPaginationState && this.infostealersPaginationState.hasNext) || false;
    },
    hasPrevious() {
      return (this.infostealersPaginationState && this.infostealersPaginationState.hasPrevious) || false;
    },
    totalPages() {
      return (this.infostealersPaginationState && this.infostealersPaginationState.totalPages) || 1;
    },
    footerProps() {
      return {
        'items-per-page-options': [20, 30, 50],
        'items-per-page-text': 'Rows per page:',
        'show-current-page': true,
        'show-first-last-page': true,
        'disable-pagination': false,
        'page-text': `Page ${this.currentPage} of ${this.totalPages}`,
      };
    },
    passwordItems() {
      const items = this.selectedEndpoint?.passwords || [];
      return this.filterPasswordItems(items);
    },
    cookieItems() {
      if (!this.selectedEndpoint || !this.selectedEndpoint.cookies) {
        return [];
      }
      const items = this.selectedEndpoint.cookies.map(cookie => {
        // Handle Discord-like cookies
        if (cookie.Application) {
          return {
            application: cookie.Application || '-',
            url: cookie.URL || '-',
            username: cookie.Username || '-',
            password: cookie.Password || '-',
            secure: cookie.Secure || '-',
            expiry: cookie.Expiry || '-',
          };
        }
        // Handle generic cookies with KEY/VALUE
        if (cookie.KEY || cookie.VALUE) {
          return {
            application: cookie.SOFT || '-',
            url: cookie.URL || '-',
            username: cookie.KEY || '-',
            password: cookie.VALUE || '-',
            secure: cookie.SECURE || '-',
            expiry: cookie.EXPIRY || '-',
          };
        }
        // Default case for unknown structure
        return {
          application: '-',
          url: '-',
          username: '-',
          password: '-',
          secure: '-',
          expiry: '-',
        };
      });
      return this.filterCookieItems(items);
    },
  },
  methods: {

    getUUID() {
      return uuidv4();
    },
    generateStableId(item) {
      const hwid = item.system?.HWID || '';
      const extractionTime = item.system?.['Extraction Time'] || '';
      const computerName = item.system?.['Computer Name'] || '';
      const ipAddress = item.system?.['IP Address'] || '';
      const user = item.system?.User || item.system?.UserName || '';

      let dataString = [hwid, extractionTime, computerName, ipAddress, user].join('|');

      if (dataString === '||||' || dataString === '') {
        dataString = JSON.stringify(item);
      }

      return crypto.createHash('sha256').update(dataString).digest('hex');
    },
    async handleSort(type, event) {
      if (type === 'column') {
        if (event.length > 0 && event[0] !== this.sortBy[0]) {
          this.sortBy = event;
          this.sortDesc[0] = false;
        } else {
          this.sortBy = event;
        }
      } else if (type === 'order' && this.sortBy[0] === 'timestamp') {
        this.sortDesc[0] = !this.sortDesc[0];
      } else if (type === 'order') {
        this.sortDesc[0] = true;
      }
      if (this.sortBy.length === 0) {
        this.sortBy[0] = 'timestamp';
        this.sortDesc[0] = true;
      }
      this.updateSorting();
    },
    async handlePageChange(newPage) {
      // Don't proceed if we're already on this page
      if (newPage === this.currentPage) return;
      // Don't proceed if we're trying to go beyond the last page
      if (newPage > this.totalPages) return;
      // Don't proceed if we're trying to go before the first page
      if (newPage < 1) return;
      await this.loadInfostealers(-1, newPage);
    },
    preselectFirstKeyword() {
      // Select the first keyword in scope (org + domain scoped). Clearing when
      // nothing is in scope makes the table show no results for a domain that is
      // not a configured keyword.
      const list = this.availableKeywords;
      this.filters.keyword = list.length > 0 ? list[0] : '';
    },
    goToInfostealersSettings() {
      this.$router.push({ name: views.SETTINGS, hash: '#brand-protection-settings' });
    },
    async loadInfostealers(itemsPerPage = -1, currentPage = -1) {
      // No keyword in scope -> don't hit the results endpoint (it 400s on empty
      // keywords and leaves the tab spinning). Clear loading and show the CTA.
      if (this.currentTab === 'infostealers' && !this.hasKeywords) {
        this.$store.commit(SET_INFOSTEALERS_LOADING, false);
        if (this.polling) {
          clearInterval(this.polling);
          this.polling = null;
        }
        return;
      }
      const table = this.$refs.mainDataTable;
      if (table) {
        table.$off();
      }
      if (this.currentTab === 'infostealers') {
        try {
          await this.$store.dispatch(CHANGE_INFOSTEALERS_LIST_OPTIONS, {
            sort: this.sortInfostealers,
            limit: itemsPerPage >= 0 ? itemsPerPage : this.itemsPerPage,
            page: currentPage >= 0 ? currentPage : this.currentPage,
            search: this.search,
            filter: this.filterSearch,
            filters: this.filters,
            hasExploit: this.hasExploit,
            turnOnLoading: true,
            tab: 'infostealers',
          });
        } catch (error) {
          console.error('Error loading infostealers:', error);
        }
      }
      if (this.infostealersRefId && this.infostealersRefId !== '') {
        if (this.polling) {
          clearInterval(this.polling);
          this.polling = null;
        }
        if (this.isSelectedKeywordScanning()) {
          // Scan still running: show the empty state instead of a long spinner.
          // The completion watcher refetches when it hits 100%.
          this.$store.commit(SET_INFOSTEALERS_LOADING, false);
        } else {
          // Scan done but results not cached yet — poll briefly to pick them up.
          this.polling = setInterval(async () => {
            if (!this.infostealersRefId || this.infostealersRefId === '') {
              clearInterval(this.polling);
              this.polling = null;
            } else if (!this.infostealersRunningInterval) {
              await this.$store.dispatch(CHANGE_INFOSTEALERS_LIST_OPTIONS, {
                sort: this.sortInfostealers,
                limit: itemsPerPage >= 0 ? itemsPerPage : this.itemsPerPage,
                page: currentPage >= 0 ? currentPage : this.currentPage,
                search: this.search,
                filter: this.filterSearch,
                filters: this.filters,
                hasExploit: this.hasExploit,
                turnOnLoading: true,
                tab: 'infostealers',
                refId: this.infostealersRefId,
              });
            }
          }, 3000);
        }
      }
    },
    // True if a scan is running for the filtered keyword (or any configured one
    // when nothing is filtered).
    isSelectedKeywordScanning() {
      const jobs = this.infostealersProgressData?.running_jobs || [];
      const isActive = job => ['pending', 'running'].includes(job.status);
      const selectedKeyword = this.filters.keyword;
      if (selectedKeyword) {
        return jobs.some(job => job.keyword === selectedKeyword && isActive(job));
      }
      const configured = configuredKeywords(this.systemSettings?.settingsMetaData?.brandProtection);
      return jobs.some(job => configured.has(job.keyword) && isActive(job));
    },
    formatDate(dateStr, displayDate) {
      const isNA = val => !val || val.trim().toUpperCase() === 'N/A';
      if (isNA(displayDate) && isNA(dateStr)) return 'N/A';
      try {
        let date;
        if (!isNA(displayDate)) {
          date = dateParser.fromString(displayDate);
        } else {
          date = dateParser.fromString(dateStr);
        }

        if (!date || Number.isNaN(date.getTime())) {
          return dateStr; // Return original if parsing fails
        }
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateStr;
      }
    },
    getTypeColor(type) {
      const colors = {
        Cryptobot: 'deep-orange',
        Blackrod: 'brown',
        AgentTesla: 'red', // Agent Tesla (RAT/stealer)&#8203;:contentReference[oaicite:5]{index=5}
        Arkei: 'red lighten-3',
        ArkeiStealer: 'red lighten-3', // Arkei Stealer (credential stealer since 2018)&#8203;:contentReference[oaicite:6]{index=6}
        AtomicStealer: 'pink', // Atomic Stealer (macOS infostealer)&#8203;:contentReference[oaicite:7]{index=7}
        AuroraStealer: 'pink lighten-3', // Aurora Stealer (Go-based infostealer)&#8203;:contentReference[oaicite:8]{index=8}
        Azorult: 'purple',
        AZORult: 'purple', // AZORult (well-known infostealer Trojan)&#8203;:contentReference[oaicite:9]{index=9}&#8203;:contentReference[oaicite:10]{index=10}
        BaldrStealer: 'purple darken-2', // Baldr Stealer (2019 infostealer malware)&#8203;:contentReference[oaicite:11]{index=11}
        BansheeStealer: 'deep-purple', // Banshee (macOS infostealer)&#8203;:contentReference[oaicite:12]{index=12}
        BHUNT: 'deep-purple lighten-3', // BHUNT (crypto-wallet stealer)&#8203;:contentReference[oaicite:13]{index=13}
        BlackGuard: 'indigo', // BlackGuard (info-stealer malware-as-a-service)&#8203;:contentReference[oaicite:14]{index=14}
        BloodyStealer: 'indigo darken-2', // BloodyStealer (steals gaming credentials)&#8203;:contentReference[oaicite:15]{index=15}
        CopperStealer: 'blue', // CopperStealer (password/cookie stealer)&#8203;:contentReference[oaicite:16]{index=16}
        Ducktail: 'blue lighten-3', // Ducktail (Facebook account stealer)&#8203;:contentReference[oaicite:17]{index=17}
        EchelonStealer: 'light-blue', // Echelon Stealer (credential stealer active since 2018)&#8203;:contentReference[oaicite:18]{index=18}
        EvilExtractor: 'light-blue darken-2', // EvilExtractor (all-in-one stealer tool)&#8203;:contentReference[oaicite:19]{index=19}
        ErbiumStealer: 'cyan', // Erbium Stealer (MaaS infostealer since 2022)&#8203;:contentReference[oaicite:20]{index=20}
        Formbook: 'cyan darken-2', // Formbook (infostealer MaaS targeting Windows)&#8203;:contentReference[oaicite:21]{index=21}
        FickerStealer: 'teal', // Ficker Stealer (Rust-based infostealer)&#8203;:contentReference[oaicite:22]{index=22}
        HawkEye: 'teal lighten-3', // HawkEye (keylogger/stealer malware)&#8203;:contentReference[oaicite:23]{index=23}
        JesterStealer: 'green', // Jester Stealer (credential stealer discovered 2021)&#8203;:contentReference[oaicite:24]{index=24}
        JupyterInfostealer: 'green darken-2', // Jupyter Infostealer (.NET infostealer since 2020)&#8203;:contentReference[oaicite:25]{index=25}
        KPotStealer: 'light-green', // KPOT Stealer (info-stealer sold on forums)&#8203;:contentReference[oaicite:26]{index=26}
        LilithBot: 'light-green darken-2', // LilithBot (Eternity group's stealer/malware)&#8203;:contentReference[oaicite:27]{index=27}
        Lokibot: 'lime',
        LokiBot: 'lime', // LokiBot (credential stealer for Windows/Android)&#8203;:contentReference[oaicite:28]{index=28}
        LucaStealer: 'lime darken-2', // Luca Stealer (open-source Rust infostealer)&#8203;:contentReference[oaicite:29]{index=29}
        LummaC2: 'purple',
        LummaStealer: 'purple', // Lumma Stealer (aka LummaC2 infostealer MaaS)&#8203;:contentReference[oaicite:30]{index=30}
        MarsStealer: 'yellow darken-3', // Mars Stealer (infostealer successor to Oski)&#8203;:contentReference[oaicite:31]{index=31}
        MassLogger: 'amber', // MassLogger (credential stealer .NET malware)&#8203;:contentReference[oaicite:32]{index=32}
        MeduzaStealer: 'amber darken-3', // Meduza Stealer (2023 emerging infostealer)&#8203;:contentReference[oaicite:33]{index=33}
        Meta: 'orange', // Meta Stealer (new RedLine-derived stealer)&#8203;:contentReference[oaicite:34]{index=34}
        'META Stealer': 'orange', // Meta Stealer (new RedLine-derived stealer)&#8203;:contentReference[oaicite:34]{index=34}
        MetaStealer: 'orange', // Meta Stealer (new RedLine-derived stealer)&#8203;:contentReference[oaicite:34]{index=34}
        OskiStealer: 'orange lighten-1', // Oski Stealer (infostealer active around 2020)&#8203;:contentReference[oaicite:35]{index=35}
        PhoenixKeylogger: 'deep-orange', // Phoenix Keylogger/Stealer (MaaS infostealer)&#8203;:contentReference[oaicite:36]{index=36}&#8203;:contentReference[oaicite:37]{index=37}
        PredatorTheThief: 'deep-orange darken-2', // Predator the Thief (infostealer, 2018)&#8203;:contentReference[oaicite:38]{index=38}&#8203;:contentReference[oaicite:39]{index=39}
        PryntStealer: 'brown', // Prynt Stealer (info-stealer with backdoor)&#8203;:contentReference[oaicite:40]{index=40}
        Raccoon: 'brown lighten-1',
        RaccoonStealer: 'brown lighten-1', // Raccoon Stealer (popular infostealer MaaS)&#8203;:contentReference[oaicite:41]{index=41}&#8203;:contentReference[oaicite:42]{index=42}
        RedLine: 'blue-grey',
        RedLineStealer: 'blue-grey', // RedLine Stealer (credential stealer since 2020)&#8203;:contentReference[oaicite:43]{index=43}&#8203;:contentReference[oaicite:44]{index=44}
        RhadamanthysStealer: 'blue-grey lighten-2', // Rhadamanthys Stealer (C++ info-stealer via maldocs)&#8203;:contentReference[oaicite:45]{index=45}
        RisePro: 'grey', // RisePro (infostealer tied to PrivateLoader)&#8203;:contentReference[oaicite:46]{index=46}
        SnakeKeylogger: 'grey darken-1', // Snake Keylogger (credentials/key strokes stealer)&#8203;:contentReference[oaicite:47]{index=47}
        SpiderManStealer: 'red accent-2', // SpiderMan Stealer (credential stealer seen on forums)&#8203;:contentReference[oaicite:48]{index=48}
        Stealc: 'purple accent-3', // Stealc (infostealer incorporating others' code)&#8203;:contentReference[oaicite:49]{index=49}
        StrelaStealer: 'green accent-4', // StrelaStealer (steals email credentials via phishing)&#8203;:contentReference[oaicite:50]{index=50}
        Taurus: 'light-green accent-3',
        TaurusStealer: 'light-green accent-3', // Taurus Stealer (aka Taurus Project, 2020)&#8203;:contentReference[oaicite:51]{index=51}
        ThirdEye: 'cyan accent-4', // ThirdEye (newer infostealer noted by researchers)&#8203;:contentReference[oaicite:52]{index=52}
        TitanStealer: 'indigo accent-3', // Titan Stealer (Telegram-sold infostealer)&#8203;:contentReference[oaicite:53]{index=53}
        Vidar: 'teal accent-4', // Vidar Stealer (MaaS infostealer since 2018)&#8203;:contentReference[oaicite:54]{index=54}&#8203;:contentReference[oaicite:55]{index=55}
        XLoader: 'amber accent-4', // XLoader (Formbook rebrand, info-stealer for Mac/Windows)&#8203;:contentReference[oaicite:56]{index=56}
        ZingoStealer: 'deep-purple accent-3', // ZingoStealer (infostealer first seen in 2022)&#8203;:contentReference[oaicite:57]{index=57}
        Zeus: 'blue accent-4', // ZeuS (banking Trojan with infostealing capability)&#8203;:contentReference[oaicite:58]{index=58}
        Chaes: 'deep-orange accent-4', // Chaes (Latin American infostealer malware)&#8203;:contentReference[oaicite:59]{index=59}
        Unknown: 'blue-grey darken-1',

      };
      return colors[type] || 'blue';
    },
    showEndpointDetails(item) {
      // If clicking the same row, toggle the panel
      if (this.selectedEndpoint &&
          item.timestamp === this.selectedEndpoint.timestamp &&
          item.username === this.selectedEndpoint.username &&
          item.hostname === this.selectedEndpoint.hostname) {
        this.selectedEndpoint = null;
      } else {
        this.selectedEndpoint = item ? { ...item } : null;
      }
    },
    closeDetails() {
      this.selectedEndpoint = null;
    },
    exportCSV() {
      const headers = [
        // Main table columns
        'Timestamp',
        'Type',
        'Username',
        'Hostname',
        'IP Address',
        'Country',
        'Keyword',
        'Severity Score',
        'Credentials Count',
        'Cookies Count',
        // System Information
        'Operating System',
        'Installation Date',
        'Timezone',
        'RAM',
        'CPU',
        // Security Status
        'Antivirus',
        'Execution Path',
        // Additional System Info
        'Machine Name',
        'Computer Name',
        'NetBIOS',
        'UTC Offset',
        'Time Zone',
      ];

      const fields = [
        // Main table fields
        'timestamp',
        'type',
        'username',
        'hostname',
        'ipAddress',
        'country',
        'keyword',
        'severity',
        'credentials',
        'cookiesCount',
        // System Information fields (mapped from system object)
        'os',
        'installDate',
        'timezone',
        'ram',
        'cpu',
        // Security Status fields
        'antivirus',
        'executionPath',
        // Additional System Info
        'machineName',
        'computerName',
        'netbios',
        'utc',
        'timeZone',
      ];

      const formatRamSize = ramValue => {
        if (!ramValue) return '-';
        const parsed = parseInt(ramValue, 10);
        if (Number.isNaN(parsed)) return '-';
        return `${(parsed / 1024).toFixed(1)} GB`;
      };

      let csvContent = `${headers.join(',')}\n`;

      this.formattedItems.forEach(item => {
        const row = fields.map(field => {
          let value = '';
          switch (field) {
          case 'severity':
            value = this.getSeverityScore(item.timestamp).toFixed(1);
            break;
          case 'cookiesCount':
            value = item.cookies.length.toString();
            break;
          case 'os':
            value = item.system['Operation System'] || item.system['OS Version'] || item.system.OS || '-';
            break;
          case 'installDate':
            value = item.system['Install Date'] || item.system['Installation Date'] || item.system['Log date'] || '-';
            break;
          case 'timezone':
            value = item.system['Time Zone'] ||
                (item.system.UTC !== undefined ? `UTC${parseInt(item.system.UTC, 10) >= 0 ? '+' : ''}${item.system.UTC}` : '-');
            break;
          case 'ram':
            value = formatRamSize(item.system['RAM Size'] || item.system.RAM);
            break;
          case 'cpu':
            value = item.system['CPU Name'] || item.system.CPU || '-';
            break;
          case 'antivirus':
            value = item.system['Anti Virus'] || '-';
            break;
          case 'executionPath':
            value = item.system.Path || item.system.FileLocation || '-';
            break;
          case 'machineName':
            value = item.system.MachineName || '-';
            break;
          case 'computerName':
            value = item.system.Computer || '-';
            break;
          case 'netbios':
            value = item.system.NetBIOS || '-';
            break;
          case 'utc':
            value = item.system.UTC !== undefined ? item.system.UTC.toString() : '-';
            break;
          case 'timeZone':
            value = item.system['Time Zone'] || '-';
            break;
          default:
            value = (item[field] || '-').toString();
          }
          return value.includes(',') || value.includes('\n') || value.includes('"')
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        });
        csvContent += `${row.join(',')}\n`;
      });

      const timestamp = this.getTimestamp();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `infostealers_detailed_export_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    exportJSON() {
      const formatRamSize = ramValue => {
        if (!ramValue) return null;
        const parsed = parseInt(ramValue, 10);
        if (Number.isNaN(parsed)) return null;
        return `${(parsed / 1024).toFixed(1)} GB`;
      };

      const exportData = this.formattedItems.map(item => {
        const ramValue = formatRamSize(item.system['RAM Size'] || item.system.RAM);

        return {
          // Main data
          timestamp: item.timestamp,
          type: item.type,
          username: item.username,
          hostname: item.hostname,
          ipAddress: item.ipAddress,
          country: item.country,
          keyword: item.keyword,
          severityScore: parseFloat(this.getSeverityScore(item.timestamp).toFixed(1)),
          credentialsCount: item.credentials,
          cookiesCount: item.cookies.length,

          // System Information
          system: {
            operatingSystem: item.system['Operation System'] || item.system['OS Version'] || item.system.OS || null,
            installationDate: item.system['Install Date'] || item.system['Installation Date'] || item.system['Log date'] || null,
            timezone: item.system['Time Zone'] || null,
            utcOffset: item.system.UTC,
            ram: ramValue,
            cpu: item.system['CPU Name'] || item.system.CPU || null,
            machineName: item.system.MachineName || null,
            computerName: item.system.Computer || null,
            netbios: item.system.NetBIOS || null,
          },

          // Security Information
          security: {
            antivirus: item.system['Anti Virus'] || null,
            executionPath: item.system.Path || item.system.FileLocation || null,
          },

          // Raw system data for reference
          rawSystemData: item.system,
        };
      });

      const timestamp = this.getTimestamp();
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `infostealers_detailed_export_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    viewRawLog() {
      this.activeTab = 0; // Ensure passwords tab is selected
      this.showRawLogDialog = true;
    },
    closeRawLog() {
      this.showRawLogDialog = false;
      this.hasKeyword = false;
      this.searchTerm = '';
      if (this.$refs.rawLogSearch) {
        this.$refs.rawLogSearch.clearSearch();
      }
    },
    viewCookiesLog() {
      this.activeTab = 1; // Cookies tab
      this.showRawLogDialog = true;
    },
    getSeverityScore(timestamp) {
      if (!timestamp) return 0;
      const date = new Date(timestamp);
      const now = new Date();
      const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
      if (diffMonths < 3) return 10.0;
      if (diffMonths < 6) return 7.5;
      if (diffMonths < 12) return 5.0;
      return 2.5;
    },
    getSeverityColor(timestamp) {
      if (!timestamp) return '#9e9e9e';
      const date = new Date(timestamp);
      const now = new Date();
      const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
      if (diffMonths < 3) return '#e0255e';
      if (diffMonths < 6) return '#ff5722';
      if (diffMonths < 12) return '#ffb74d';
      return '#80cbc4';
    },
    rowClasses(item) {
      if (!this.selectedEndpoint) return '';
      return {
        'selected-row': item.timestamp === this.selectedEndpoint.timestamp &&
                       item.username === this.selectedEndpoint.username &&
                       item.hostname === this.selectedEndpoint.hostname,
      };
    },
    async copyToClipboard(text, event) {
      if (event) {
        event.stopPropagation();
      }
      try {
        await navigator.clipboard.writeText(text);
        this.snackbar.color = 'success';
        this.snackbar.text = 'Copied!';
        this.snackbar.icon = 'mdi-check-circle';
        this.snackbar.show = true;
      } catch (err) {
        console.error('Failed to copy text: ', err);
        this.snackbar.color = 'error';
        this.snackbar.text = 'Failed to copy';
        this.snackbar.icon = 'mdi-alert-circle';
        this.snackbar.show = true;
      }
    },
    updateItemsPerPage(value) {
      this.loadInfostealers(value, 1);
    },
    exportCurrentTabCSV() {
      const data = this.activeTab === 0 ? this.passwordItems : this.cookieItems;
      const prefix = this.activeTab === 0 ? 'passwords' : 'cookies';

      // Define headers and fields based on the active tab
      let headers;
      let fields;
      if (this.activeTab === 0) {
        // Password export
        headers = ['Software', 'URL', 'Username', 'Password'];
        fields = ['SOFT', 'URL', 'USER', 'PASS'];
      } else {
        // Cookie export
        headers = ['Application', 'URL', 'Key', 'Value', 'Secure', 'Expiry'];
        fields = ['application', 'url', 'username', 'password', 'secure', 'expiry'];
      }

      let csvContent = `${headers.join(',')}\n`;
      data.forEach(item => {
        const row = fields.map(field => {
          const value = item[field] || '';
          return value.includes(',') || value.includes('\n') || value.includes('"')
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        });
        csvContent += `${row.join(',')}\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const timestamp = this.getTimestamp();
      link.setAttribute('download', `${prefix}_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    exportCurrentTabJSON() {
      const data = this.activeTab === 0 ? this.passwordItems : this.cookieItems;
      const prefix = this.activeTab === 0 ? 'passwords' : 'cookies';
      const timestamp = this.getTimestamp();
      const jsonContent = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${prefix}_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    getTimestamp() {
      const date = new Date();
      return date.toISOString().split('.')[0].replace(/[:-]/g, '');
    },

    showInfostealerDetails(type) {
      // First try with the original name
      if (this.infostealerDetails[type]) {
        this.selectedInfostealer = type;
        this.showInfostealerDialog = true;
        return;
      }

      // If not found, try removing spaces and "Stealer" suffix
      const normalizedType = type
        .replace(/\s+/g, '') // Remove all spaces
        .replace(/Stealer$/, ''); // Remove "Stealer" suffix if present

      // Try to find a match in the infostealer details
      const matchingKey = Object.keys(this.infostealerDetails).find(key => {
        const normalizedKey = key
          .replace(/\s+/g, '')
          .replace(/Stealer$/, '');
        return normalizedKey === normalizedType;
      });

      if (matchingKey) {
        this.selectedInfostealer = matchingKey;
        this.showInfostealerDialog = true;
      } else {
        // If still not found, show the dialog with the original name
        this.selectedInfostealer = type;
        this.showInfostealerDialog = true;
      }
    },
    closeInfostealerDialog() {
      this.showInfostealerDialog = false;
      this.selectedInfostealer = null;
    },
    parseNumberedList(text) {
      if (!text) return [];
      // Split by number followed by period and space
      return text.split(/\d+\.\s+/).filter(item => item.trim());
    },
    showVisualizations() {
      this.showVisualizationDialog = true;
      // Wait for dialog to be mounted
      this.$nextTick(() => {
        setTimeout(() => {
          this.createCharts();
        }, 300);
      });
    },

    createCharts() {
      try {
        this.cleanupCharts();
        this.createPieChart();
        this.createBarChart();
        this.createLineChart();
        this.createStackedChart();
        this.createOSChart();
        this.createKeywordChart();
      } catch (error) {
        console.error('Error creating charts:', error);
      }
    },

    closeVisualizations() {
      this.showVisualizationDialog = false;
      this.cleanupCharts();
    },

    cleanupCharts() {
      try {
        if (this.$refs.pieChart) this.$refs.pieChart.innerHTML = '';
        if (this.$refs.barChart) this.$refs.barChart.innerHTML = '';
        if (this.$refs.lineChart) this.$refs.lineChart.innerHTML = '';
        if (this.$refs.stackedChart) this.$refs.stackedChart.innerHTML = '';
        if (this.$refs.osChart) this.$refs.osChart.innerHTML = '';
        if (this.$refs.keywordChart) this.$refs.keywordChart.innerHTML = '';
      } catch (error) {
        console.error('Error cleaning up charts:', error);
      }
    },

    createPieChart() {
      const data = this.getInfostealerDistribution();
      const container = this.$refs.pieChart;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const margin = {
        top: 20,
        right: 160,
        bottom: 20,
        left: 20,
      }; // Increased right margin for legend
      const radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) / 2;

      // Clear any existing chart
      container.innerHTML = '';

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .append('g')
        .attr('transform', `translate(${(width - margin.right) / 2},${height / 2})`);

      // Use the same color function from the main table
      const color = d => this.getTypeColor(d.type);

      const pie = d3.pie()
        .value(d => d.count)
        .sort(null);

      const arc = d3.arc()
        .innerRadius(0)
        .outerRadius(radius);

      const arcs = svg.selectAll('arc')
        .data(pie(data))
        .enter()
        .append('g')
        .attr('class', 'arc');

      arcs.append('path')
        .attr('d', arc)
        .attr('fill', d => color(d.data))
        .attr('stroke', 'white')
        .style('stroke-width', '2px');

      // Add percentage labels
      arcs.append('text')
        .attr('transform', d => `translate(${arc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .attr('dy', '.35em')
        .style('fill', 'white')
        .style('font-size', '12px')
        .text(d => {
          const total = data.reduce((sum, item) => sum + item.count, 0);
          const fraction = d.data.count / total;
          const percent = (fraction * 100).toFixed(1);
          return percent > 5 ? `${percent}%` : '';
        });

      // Add legend with improved text handling
      const legendSpacing = 20;
      const legendRectSize = 12;
      const legendTextOffset = 20;

      const legend = svg.selectAll('.legend')
        .data(data)
        .enter()
        .append('g')
        .attr('class', 'legend')
        .attr('transform', (d, i) => `translate(${radius + 40},${-radius + (i * legendSpacing)})`);

      legend.append('rect')
        .attr('width', legendRectSize)
        .attr('height', legendRectSize)
        .attr('fill', d => color(d));

      // Add legend text with truncation if needed
      legend.append('text')
        .attr('x', legendTextOffset)
        .attr('y', legendRectSize - 1)
        .style('font-size', '12px')
        .each(function (d) {
          const text = d3.select(this);
          const textContent = `${d.type} (${d.count})`;
          const maxLength = 25; // Maximum characters before truncation

          if (textContent.length > maxLength) {
            const truncatedText = `${textContent.substring(0, maxLength - 3)}...`;
            text.text(truncatedText);
          } else {
            text.text(textContent);
          }

          text.text(textContent);
        });
    },
    createBarChart() {
      const data = this.getTopCountries();
      const container = this.$refs.barChart;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const margin = {
        top: 20, right: 30, bottom: 60, left: 60,
      };

      // Clear any existing chart
      container.innerHTML = '';

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      const x = d3.scaleBand()
        .range([margin.left, width - margin.right])
        .padding(0.2)
        .domain(data.map(d => d.country));

      const y = d3.scaleLinear()
        .range([height - margin.bottom, margin.top])
        .domain([0, d3.max(data, d => d.count) * 1.1]); // Add 10% padding

      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end')
        .style('font-size', '12px');

      // Add Y axis
      svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y))
        .selectAll('text')
        .style('font-size', '12px');

      // Add bars
      svg.selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', d => x(d.country))
        .attr('y', d => y(d.count))
        .attr('width', x.bandwidth())
        .attr('height', d => height - margin.bottom - y(d.count))
        .attr('fill', '#2196F3');

      // Add value labels on top of bars
      svg.selectAll('.value-label')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'value-label')
        .attr('x', d => x(d.country) + x.bandwidth() / 2)
        .attr('y', d => y(d.count) - 5)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .text(d => d.count);
    },
    getInfectionsOverTime() {
      const timeline = {};
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      oneYearAgo.setDate(1); // Set to first day of the month
      oneYearAgo.setHours(0, 0, 0, 0);

      // Create entries for all months in the past year
      const currentDate = new Date();
      for (const d = new Date(oneYearAgo); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
        const monthKey = new Date(d).toISOString().split('T')[0];
        timeline[monthKey] = {
          date: new Date(d),
          count: 0,
        };
      }

      // Count infections by month
      this.formattedItems.forEach(item => {
        if (!item.timestamp || item.timestamp === 'N/A') return;
        try {
          const parsedDate = this.parseTimestamp(item.timestamp);
          if (!parsedDate || Number.isNaN(parsedDate.getTime())) return;
          if (parsedDate < oneYearAgo) return;

          // Set to first day of the month
          const monthDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1);
          const monthKey = monthDate.toISOString().split('T')[0];

          if (timeline[monthKey]) {
            timeline[monthKey].count += 1;
          }
        } catch (error) {
          console.error('Error processing date for timeline:', error);
        }
      });

      // Convert to array and sort by date
      return Object.values(timeline).sort((a, b) => a.date - b.date);
    },
    createLineChart() {
      const data = this.getInfectionsOverTime();
      const container = this.$refs.lineChart;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const margin = {
        top: 20, right: 30, bottom: 80, left: 60,
      };

      // Clear any existing chart
      container.innerHTML = '';

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      // Add background grid
      const x = d3.scaleTime()
        .range([margin.left, width - margin.right])
        .domain(d3.extent(data, d => d.date));

      const y = d3.scaleLinear()
        .range([height - margin.bottom, margin.top])
        .domain([0, d3.max(data, d => d.count) * 1.1]);

      // Add gridlines
      svg.append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(
          d3.axisBottom(x)
            .tickSize(-(height - margin.top - margin.bottom))
            .tickFormat(''),
        )
        .style('stroke-dasharray', '3,3')
        .style('stroke-opacity', 0.1);

      svg.append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(${margin.left},0)`)
        .call(
          d3.axisLeft(y)
            .tickSize(-(width - margin.left - margin.right))
            .tickFormat(''),
        )
        .style('stroke-dasharray', '3,3')
        .style('stroke-opacity', 0.1);

      const xAxis = svg.append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`);

      // Show all months
      xAxis.call(d3.axisBottom(x)
        .tickValues(data.map(d => d.date))
        .tickFormat(d => {
          const date = new Date(d);
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
          return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        }))
        .selectAll('text')
        .style('font-size', '10px')
        .attr('transform', 'rotate(-45)')
        .attr('x', -8)
        .attr('y', 8)
        .style('text-anchor', 'end');

      // Add Y axis
      svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y)
          .ticks(5)
          .tickFormat(d => Math.round(d)))
        .selectAll('text')
        .style('font-size', '12px');

      // Add the line
      const line = d3.line()
        .x(d => x(d.date))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);

      svg.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', '#2196F3')
        .attr('stroke-width', 2)
        .attr('d', line);

      // Add dots
      const dots = svg.selectAll('.dot')
        .data(data)
        .enter()
        .append('g')
        .attr('class', 'dot')
        .attr('transform', d => `translate(${x(d.date)},${y(d.count)})`);

      dots.append('circle')
        .attr('r', 4)
        .attr('fill', '#2196F3');

      // Add value labels above points
      dots.append('text')
        .attr('dy', -10)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .text(d => d.count);

      // Add chart title
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', margin.top)
        .attr('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('font-weight', 'bold')
        .text('Monthly Infection Count (Last 12 Months)');
    },
    createStackedChart() {
      const data = this.getCompromisedData();
      const container = this.$refs.stackedChart;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const margin = {
        top: 20,
        right: 120,
        bottom: 100,
        left: 60,
      };

      // Clear any existing chart
      container.innerHTML = '';

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      const x = d3.scaleBand()
        .range([margin.left, width - margin.right])
        .padding(0.1)
        .domain(data.map(d => d.type));

      const y = d3.scaleLinear()
        .range([height - margin.bottom, margin.top])
        .domain([0, d3.max(data, d => d.credentials + d.cookies) * 1.1]);

      const color = d3.scaleOrdinal()
        .domain(['credentials', 'cookies'])
        .range(['#E132A0', '#FBA212']);

      // Add X axis with improved label handling
      svg.append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .attr('x', -10)
        .attr('y', 10)
        .style('text-anchor', 'end')
        .style('font-size', '12px')
        .each(function (d) {
          const text = d3.select(this);
          text.text(null);

          // If the name contains "Stealer", split it into two lines
          if (d.includes('Stealer')) {
            const parts = d.split('Stealer');
            text.append('tspan')
              .attr('x', -10)
              .text(parts[0]);
            text.append('tspan')
              .attr('x', -10)
              .attr('dy', '1.2em')
              .text('Stealer');
          } else {
            // For other names, keep them on a single line
            text.append('tspan')
              .attr('x', -10)
              .text(d);
          }
        });

      // Add Y axis
      svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y))
        .selectAll('text')
        .style('font-size', '12px');

      const stack = d3.stack()
        .keys(['credentials', 'cookies']);

      const series = stack(data);

      // Add the stacked bars
      svg.selectAll('g.series')
        .data(series)
        .enter()
        .append('g')
        .attr('class', 'series')
        .attr('fill', d => color(d.key))
        .selectAll('rect')
        .data(d => d)
        .enter()
        .append('rect')
        .attr('x', d => x(d.data.type))
        .attr('y', d => y(d[1]))
        .attr('height', d => y(d[0]) - y(d[1]))
        .attr('width', x.bandwidth());

      // Add legend
      const legend = svg.append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${width - margin.right + 10},${margin.top})`);

      ['Credentials', 'Cookies'].forEach((key, i) => {
        const legendRow = legend.append('g')
          .attr('transform', `translate(0,${i * 20})`);

        legendRow.append('rect')
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', color(key.toLowerCase()));

        legendRow.append('text')
          .attr('x', 20)
          .attr('y', 9)
          .style('font-size', '12px')
          .text(key);
      });
    },
    getInfostealerDistribution() {
      const distribution = {};
      this.formattedItems.forEach(item => {
        distribution[item.type] = (distribution[item.type] || 0) + 1;
      });
      return Object.entries(distribution).map(([type, count]) => ({ type, count }));
    },
    getTopCountries() {
      const countries = {};
      this.formattedItems.forEach(item => {
        if (item.country !== 'N/A') {
          countries[item.country] = (countries[item.country] || 0) + 1;
        }
      });
      return Object.entries(countries)
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
    getCompromisedData() {
      const data = {};
      this.formattedItems.forEach(item => {
        if (!data[item.type]) {
          data[item.type] = {
            type: item.type,
            credentials: 0,
            cookies: 0,
          };
        }
        data[item.type].credentials += item.credentials;
        data[item.type].cookies += item.cookies.length;
      });
      return Object.values(data);
    },
    createOSChart() {
      const data = this.getOperatingSystemDistribution();
      const container = this.$refs.osChart;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const margin = {
        top: 20,
        right: 120,
        bottom: 100,
        left: 60,
      };

      // Clear any existing chart
      container.innerHTML = '';

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      const x = d3.scaleBand()
        .range([margin.left, width - margin.right])
        .padding(0.1)
        .domain(data.map(d => d.os));

      const y = d3.scaleLinear()
        .range([height - margin.bottom, margin.top])
        .domain([0, d3.max(data, d => d.count) * 1.1]);

      // Add X axis with smaller font
      svg.append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .style('font-size', '8px')
        .attr('transform', 'rotate(-45)')
        .attr('x', -10)
        .attr('y', 10)
        .style('text-anchor', 'end');

      // Add Y axis
      svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y))
        .selectAll('text')
        .style('font-size', '12px');

      // Add bars
      svg.selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', d => x(d.os))
        .attr('y', d => y(d.count))
        .attr('width', x.bandwidth())
        .attr('height', d => height - margin.bottom - y(d.count))
        .attr('fill', '#4CAF50');

      // Add value labels on top of bars
      svg.selectAll('.value-label')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'value-label')
        .attr('x', d => x(d.os) + x.bandwidth() / 2)
        .attr('y', d => y(d.count) - 5)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .text(d => d.count);
    },

    createKeywordChart() {
      const data = this.getKeywordDistribution();
      const container = this.$refs.keywordChart;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const margin = {
        top: 20,
        right: 120,
        bottom: 100,
        left: 60,
      };

      // Clear any existing chart
      container.innerHTML = '';

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      const x = d3.scaleBand()
        .range([margin.left, width - margin.right])
        .padding(0.1)
        .domain(data.map(d => d.keyword));

      const y = d3.scaleLinear()
        .range([height - margin.bottom, margin.top])
        .domain([0, d3.max(data, d => d.count) * 1.1]);

      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .attr('x', -10)
        .attr('y', 10)
        .style('text-anchor', 'end')
        .style('font-size', '12px');

      // Add Y axis
      svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y))
        .selectAll('text')
        .style('font-size', '12px');

      // Add bars
      svg.selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', d => x(d.keyword))
        .attr('y', d => y(d.count))
        .attr('width', x.bandwidth())
        .attr('height', d => height - margin.bottom - y(d.count))
        .attr('fill', '#9C27B0');

      // Add value labels on top of bars
      svg.selectAll('.value-label')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'value-label')
        .attr('x', d => x(d.keyword) + x.bandwidth() / 2)
        .attr('y', d => y(d.count) - 5)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .text(d => d.count);
    },

    getOperatingSystemDistribution() {
      const osCount = {};
      this.formattedItems.forEach(item => {
        const os = item.system['Operation System'] ||
                  item.system['OS Version'] ||
                  item.system.OS ||
                  'Unknown';
        if (os !== 'N/A') {
          osCount[os] = (osCount[os] || 0) + 1;
        }
      });
      return Object.entries(osCount)
        .map(([os, count]) => ({ os, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Show top 10 OS
    },

    getKeywordDistribution() {
      const keywordCount = {};
      this.formattedItems.forEach(item => {
        const keyword = item.keyword || 'Unknown';
        if (keyword !== '-') {
          keywordCount[keyword] = (keywordCount[keyword] || 0) + 1;
        }
      });
      return Object.entries(keywordCount)
        .map(([keyword, count]) => ({ keyword, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Show top 10 keywords
    },
    parseTimestamp(dateStr) {
      if (!dateStr || dateStr === 'N/A') return null;
      try {
        // Handle AM/PM format
        if (dateStr.includes('PM') || dateStr.includes('AM')) {
          const [datePart, timePart, period] = dateStr.split(' ');
          const [month, day, year] = datePart.split('/');
          const [hours, minutes] = timePart.split(':');
          let hour = parseInt(hours, 10);
          if (period === 'PM' && hour < 12) hour += 12;
          if (period === 'AM' && hour === 12) hour = 0;
          return new Date(year, month - 1, day, hour, parseInt(minutes, 10));
        }

        // Handle MM/DD/YYYY HH:mm:ss format
        const [datePart, timePart] = dateStr.split(' ');
        if (datePart.includes('/')) {
          const [month, day, year] = datePart.split('/');
          if (timePart) {
            const [hours, minutes, seconds] = timePart.split(':');
            return new Date(
              year,
              month - 1,
              day,
              parseInt(hours, 10),
              parseInt(minutes, 10),
              seconds ? parseInt(seconds, 10) : 0,
            );
          }
          return new Date(year, month - 1, day);
        }

        // Handle DD.MM.YYYY HH:mm:ss format
        if (datePart.includes('.')) {
          const [day, month, year] = datePart.split('.');
          if (timePart) {
            const [hours, minutes, seconds] = timePart.split(':');
            return new Date(
              year,
              month - 1,
              day,
              parseInt(hours, 10),
              parseInt(minutes, 10),
              seconds ? parseInt(seconds, 10) : 0,
            );
          }
          return new Date(year, month - 1, day);
        }

        // Fallback to standard date parsing
        return new Date(dateStr);
      } catch (error) {
        console.error('Error parsing timestamp:', error);
        return null;
      }
    },
    exportStatisticsHTML() {
      // Get all chart SVGs
      const pieChartSVG = this.$refs.pieChart?.querySelector('svg')?.outerHTML || '';
      const barChartSVG = this.$refs.barChart?.querySelector('svg')?.outerHTML || '';
      const osChartSVG = this.$refs.osChart?.querySelector('svg')?.outerHTML || '';
      const keywordChartSVG = this.$refs.keywordChart?.querySelector('svg')?.outerHTML || '';
      const lineChartSVG = this.$refs.lineChart?.querySelector('svg')?.outerHTML || '';
      const stackedChartSVG = this.$refs.stackedChart?.querySelector('svg')?.outerHTML || '';

      // Create HTML template
      const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Infostealers Statistics</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: #f5f5f5;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            padding: 20px;
            background: white;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .header h1 {
            margin: 0;
            color: #333;
        }
        .chart-container {
            background: white;
            border-radius: 4px;
            padding: 24px;
            margin-bottom: 32px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .chart-title {
            margin: 0 0 24px;
            font-size: 1.1rem;
            font-weight: 500;
            color: rgba(0,0,0,0.87);
        }
        .chart-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
        }
        .chart-full {
            grid-column: span 2;
        }
        .timestamp {
            text-align: center;
            color: #666;
            margin-top: 20px;
            font-size: 0.9rem;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Infostealers Statistics Report</h1>
        </div>
        <div class="chart-grid">
            <div class="chart-container">
                <h3 class="chart-title">INFOSTEALER DISTRIBUTION</h3>
                ${pieChartSVG}
            </div>
            <div class="chart-container">
                <h3 class="chart-title">TOP AFFECTED COUNTRIES</h3>
                ${barChartSVG}
            </div>
            <div class="chart-container">
                <h3 class="chart-title">TOP INFECTED OPERATING SYSTEMS</h3>
                ${osChartSVG}
            </div>
            <div class="chart-container">
                <h3 class="chart-title">KEYWORD DISTRIBUTION</h3>
                ${keywordChartSVG}
            </div>
            <div class="chart-container chart-full">
                <h3 class="chart-title">INFECTIONS TIMELINE</h3>
                ${lineChartSVG}
            </div>
            <div class="chart-container chart-full">
                <h3 class="chart-title">COMPROMISED DATA TYPES</h3>
                ${stackedChartSVG}
            </div>
        </div>
        <div class="timestamp">
            Generated on: ${new Date().toLocaleString()}
        </div>
    </div>
</body>
</html>`;

      // Create blob and download
      const blob = new Blob([html], { type: 'text/html' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('.')[0].replace(/[:-]/g, '');

      link.setAttribute('href', url);
      link.setAttribute('download', `infostealers_statistics_${timestamp}.html`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    updateSorting() {
      this.sortInfostealers = {
        by: this.sortBy,
        desc: this.sortDesc,
      };
      this.loadInfostealers(-1, 1);
    },
    updateFiltering() {
      this.loadInfostealers(-1, 1);
    },
    // Raw log dialog search methods
    searchChange(e) {
      this.searchTerm = e.trim();
    },
    filterPasswordItems(items) {
      if (!items || items.length === 0) return items;

      let filteredItems = items;

      // Apply hasKeyword filter if toggle is on
      if (this.hasKeyword) {
        filteredItems = filteredItems.filter(item => {
          // Password-specific searchable fields
          const searchableFields = [item.URL, item.USER, item.PASS];
          // Check if the selected endpoint's keyword appears in any of the searchable fields
          const endpointKeyword = this.selectedEndpoint?.keyword;
          if (!endpointKeyword || endpointKeyword === '-') {
            return false;
          }

          return searchableFields.some(field => {
            if (!field || field.trim() === '' || field === '-') {
              return false;
            }
            return field.includes(`${endpointKeyword}`);
          });
        });
      }

      // Apply search term filter if there's a search term
      const searchTerm = this.searchTerm.toLowerCase().trim();
      if (searchTerm) {
        filteredItems = filteredItems.filter(item => {
          // Focus on URL filtering as requested
          if (item.URL && item.URL.toLowerCase().includes(searchTerm)) {
            return true;
          }

          // Also search in other relevant password fields
          const searchableFields = [item.USER, item.PASS];
          return searchableFields.some(field => field && field.toLowerCase().includes(searchTerm));
        });
      }
      return filteredItems;
    },

    filterCookieItems(items) {
      if (!items || items.length === 0) return items;

      let filteredItems = items;
      // Apply hasKeyword filter if toggle is on
      if (this.hasKeyword) {
        filteredItems = filteredItems.filter(item => {
          // Cookie-specific searchable fields
          const searchableFields = [item.application, item.url, item.username, item.password, item.secure, item.expiry];
          // Check if the selected endpoint's keyword appears in any of the searchable fields
          const endpointKeyword = this.selectedEndpoint?.keyword;
          if (!endpointKeyword || endpointKeyword === '-') {
            return false;
          }
          return searchableFields.some(field => {
            if (!field || field.trim() === '' || field === '-') {
              return false;
            }
            return field.includes(`${endpointKeyword}`);
          });
        });
      }

      // Apply search term filter if there's a search term
      const searchTerm = this.searchTerm.toLowerCase().trim();
      if (searchTerm) {
        filteredItems = filteredItems.filter(item => {
          // Focus on URL filtering as requested
          if (item.url && item.url.toLowerCase().includes(searchTerm)) {
            return true;
          }

          // Also search in other relevant cookie fields
          const searchableFields = [item.application, item.username, item.password, item.secure, item.expiry];
          return searchableFields.some(field => field && field.toLowerCase().includes(searchTerm));
        });
      }
      return filteredItems;
    },
  },
  watch: {
    async currentTab() {
      if (this.currentTab === 'infostealers') {
        this.preselectFirstKeyword();
        await this.loadInfostealers();
      }
    },
    async organizationFilter() {
      if (this.currentTab === 'infostealers') {
        this.filters = {
          type: '',
          country: '',
          os: '',
          username: '',
          hostname: '',
          ip: '',
          keyword: '',
        };
        this.$store.commit(SET_INFOSTEALERS_FILTERS, {});
        this.preselectFirstKeyword();
        await this.loadInfostealers();
      }
    },
    async domainFilter() {
      // Selected domain(s) scope the keyword dropdown (R10). Re-preselect within
      // the new scope and reload results (which triggers the scan server-side).
      if (this.currentTab === 'infostealers') {
        this.preselectFirstKeyword();
        await this.loadInfostealers();
      }
    },
    filters: {
      handler: 'updateFiltering',
      immediate: false,
      deep: true,
    },
    // Status polling updates infostealersProgressData. When a scan for a
    // configured keyword transitions from running to finished (the status hits
    // 100%), refetch the tab's data once so the freshly-scanned results replace
    // the in-progress view without a manual page refresh. Guards keep it safe:
    // it only acts on the active -> idle edge, only while on the infostealers
    // tab, and never loops or fires on plain idle polls.
    // Refetch once when the in-scope keyword's scan finishes (hits 100%).
    infostealersProgressData: {
      handler() {
        if (this.currentTab !== 'infostealers') {
          return;
        }
        const activeNow = this.isSelectedKeywordScanning();
        if (this.scanWasActive && !activeNow) {
          this.loadInfostealers(-1, 1);
        }
        this.scanWasActive = activeNow;
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.infostealers-tab-loading {
  position: relative;
  min-height: calc(100vh - 200px);
}

.infostealers-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - #{rem(260)});
  padding: $space-6;

  &__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: $space-4;
    max-width: rem(460);
    padding: $space-10 $space-7;
    background: var(--bg-widget);
    border: 1px solid var(--border-secondary);
    border-radius: $radius-2xl;
    box-shadow: $shadow-sm;
  }

  &__badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(80);
    height: rem(80);
    border-radius: $radius-full;
    background: var(--bg-tertiary);
  }

  &__title {
    margin: 0;
    font-size: $font-size-2xl;
    font-weight: 600;
  }

  &__text {
    margin: 0;
    color: var(--text-secondary);
    line-height: 1.6;
  }

  &__cta {
    margin-top: $space-2;
    text-transform: none;
    letter-spacing: 0;
  }
}

.license-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - #{rem(205)});
}

.infostealers-tab {
  padding: $space-5;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display

  &__header {
    margin-bottom: $space-5;

    .filters-container {
      display: flex;
      align-items: flex-end;
      gap: $space-4;
      flex-wrap: wrap;
      position: relative;

      .filter-item {
        flex: 1;
        min-width: rem(150);
        max-width: rem(200);

        label {
          display: block;
          margin-bottom: $space-1;
          font-size: $font-size-base;
          color: var(--text-secondary);
        }
      }

      .header-actions {
        display: flex;
        gap: $space-2;
        margin-bottom: $space-1;
        margin-left: auto;

        .export-btn.v-btn.v-btn {
          background-color: transparent !important;
          box-shadow: none !important;
          color: var(--text-secondary) !important;

          &:hover {
            background-color: var(--bg-tertiary) !important;
            color: var(--text-primary) !important;
          }
        }
      }
    }
  }

  &__filters {
    margin-bottom: $space-5;

    .filters-container {
      display: flex;
      gap: $space-4;
      max-width: 50%;

      .filter-item {
        flex: 1;
        min-width: rem(150);
        max-width: rem(200);

        label {
          display: block;
          margin-bottom: $space-1;
          font-size: $font-size-base;
          color: var(--text-secondary);
        }
      }
    }
  }

  .infostealers-content {
    flex: 1;
    min-height: 0;

    .content-wrapper {
      display: flex;
      gap: $space-6;
      height: 100%;

      .table-section {
        flex: 1;
        min-width: 0;

        .results-count {
          margin-bottom: $space-3;
          font-size: $font-size-base;
          color: var(--text-secondary);
          font-weight: 500;
        }
      }

      .details-section {
        width: rem(600);
        background: var(--bg-primary);
        border-radius: $radius-sm;
        box-shadow: $shadow-sm;
        display: flex;
        flex-direction: column;

        .details-header {
          padding: $space-4;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: $border-width-thin solid var(--border-primary);

          h3 {
            margin: 0;
            font-size: $font-size-lg;
            font-weight: 500;
          }
        }

        .details-content {
          flex: 1;
          overflow-y: auto;
          padding: $space-4;

          h4 {
            margin: 0 0 $space-4;
            font-size: $font-size-md;
            font-weight: 500;
          }

          .basic-info {
            margin-bottom: $space-6;

            .info-row {
              display: flex;
              align-items: center;
              margin-bottom: $space-3;

              .info-label {
                width: rem(100);
                color: var(--text-secondary);
              }
            }
          }

          .system-info {
            margin-bottom: $space-6;

            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: $space-4;

              .info-item {
                display: flex;
                align-items: flex-start;

                .info-label {
                  font-size: $font-size-xs;
                  color: var(--text-secondary);
                  margin-bottom: $space-1;
                }

                .info-value {
                  font-size: $font-size-base;
                }
              }
            }
          }

          .security-status {
            margin-bottom: $space-6;

            .status-row {
              display: flex;
              align-items: center;
              gap: $space-2;
              margin-bottom: $space-3;

              span {
                font-size: $font-size-base;
              }
            }

            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: $space-4;

              .info-item {
                display: flex;
                align-items: flex-start;

                .info-label {
                  font-size: $font-size-xs;
                  color: var(--text-secondary);
                  margin-bottom: $space-1;
                }

                .info-value {
                  font-size: $font-size-base;
                }
              }
            }
          }

          .compromised-data-section {
            margin-bottom: $space-6;

            .data-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: $space-4;

              .data-item {
                text-align: center;
                padding: $space-4;
                border-radius: $radius-sm;
                background: var(--bg-tertiary);

                &.credentials {
                  .count {
                    color: var(--severity-critical);
                  }
                }

                &.cookies {
                  .count {
                    color: var(--warning-color);
                  }
                }

                &.cards {
                  .count {
                    color: var(--text-primary);
                  }
                }

                .count {
                  font-size: $font-size-2xl;
                  font-weight: 500;
                  margin-bottom: $space-1;
                }

                .label {
                  font-size: $font-size-base;
                  color: var(--text-secondary);
                }
              }
            }
          }

          .severity-section {
            margin-bottom: $space-6;

            .severity-score {
              font-size: $font-size-2xl;
              font-weight: 500;
            }
          }

          .details-actions {
            display: flex;
            justify-content: flex-end;
            gap: $space-2;
          }
        }
      }
    }
  }

  :deep(.v-data-table) {
    background-color: transparent !important;
    box-shadow: none !important;

    .v-data-table__wrapper {
      background-color: transparent !important;
    }

    .v-data-footer {
      background-color: transparent !important;
      color: var(--text-primary);
    }

    thead {
      th {
        background-color: transparent !important;
        color: var(--text-secondary) !important;
        text-align: left !important;
        vertical-align: middle !important;
        padding-top: rem(12) !important;
        padding-bottom: rem(12) !important;
      }
    }

    tbody {
      tr {
        background-color: transparent !important;
      }

      tr.selected-row {
        background-color: rgba(229, 109, 246, 0.08) !important;
        border-left: $space-1 solid var(--brand-primary);
      }

      tr.selected-row:hover {
        background-color: var(--sidebar-active-bg) !important;
      }

      tr:hover {
        background-color: var(--bg-tertiary) !important;
      }

      td {
        height: $space-12;
        vertical-align: middle;
        text-align: left;
      }

      tr.selected-row td:first-child {
        padding-left: $space-3;
      }
    }

    th {
      text-align: left !important;
      background-color: transparent !important;
    }
  }

  .compromised-data {
    display: flex;
    align-items: center;
    gap: $space-3;

    .v-chip {
      font-size: $font-size-base;
      font-weight: 500;
      padding: 0 $space-3;
    }

    // Dimmed semantic colors — match the drawer's data-item style
    .compromised-chip {
      font-size: $font-size-base;
      font-weight: 500;
      padding: 0 $space-3;

      &.chip-credentials {
        background-color: rgba(225, 50, 160, 0.15) !important;
        color: #E132A0 !important;
      }

      &.chip-cookies {
        background-color: rgba(251, 162, 18, 0.15) !important;
        color: #FBA212 !important;
      }

      &.chip-zero {
        background-color: var(--bg-tertiary) !important;
        color: var(--text-secondary) !important;
      }
    }
  }

  .v-data-table {
    .v-data-table__wrapper {
      max-height: rem(600);
      overflow-y: auto;
    }

    :deep(td) {
      vertical-align: middle;
      text-align: left;
    }

    :deep(th) {
      text-align: left;
    }
  }

  :deep(.v-tabs) {
    margin-bottom: -$space-4;
  }

  .clickable-cell {
    cursor: pointer;
    position: relative;

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.6;
    }
  }

  :deep(.v-snack__wrapper) {
    min-width: unset;
    min-height: unset;
    padding: $space-2 $space-4;
    border-radius: $radius-sm;
  }

  :deep(.v-snack__content) {
    padding: 0;
  }
}

.close-button {
  border: none;
  background: none;
  font-size: $font-size-2xl;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0 $space-2;
  line-height: 1;
}

// Global styles for clickable cells
:deep(.clickable-cell) {
  cursor: pointer;
  position: relative;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
}

.v-dialog {
  .v-data-table {
    :deep(td) {
      white-space: normal;
      word-break: break-word;
      max-width: 0;

      .clickable-cell {
        cursor: pointer;
        display: block;
        word-wrap: break-word;

        &:hover {
          opacity: 0.8;
        }

        &:active {
          opacity: 0.6;
        }
      }
    }

    // Specific styling for URL columns
    :deep(td:nth-child(2)) {
      min-width: rem(300);
    }
  }
}

// Main table styles
:deep(.v-data-table) {
  table {
    table-layout: fixed;
  }

  td {
    white-space: normal;
    word-break: break-word;
    max-width: 0;
  }
}

.section-title {
  color: var(--text-primary);
  font-size: $font-size-lg;
  font-weight: 500;
}

.section {
  p {
    color: var(--text-secondary);
    line-height: 1.6;
    margin: 0;
  }
}

.technical-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $space-4;
  margin-top: $space-2;

  .technical-detail-item {
    .detail-label {
      font-size: $font-size-xs;
      color: var(--text-secondary);
      margin-bottom: $space-1;
    }

    .detail-value {
      font-size: $font-size-base;
      color: var(--text-primary);
      word-break: break-word;
    }
  }
}

.ioc-list,
.mitigation-list {
  list-style-type: none;
  padding-left: 0;
  margin: $space-2 0;

  .ioc-item,
  .mitigation-item {
    position: relative;
    padding-left: $space-5;
    margin-bottom: $space-2;
    font-size: $font-size-base;
    color: var(--text-primary);
    line-height: 1.6;

    &:before {
      content: "•";
      position: absolute;
      left: $space-2;
      color: var(--text-secondary);
    }
  }
}

.section-content {
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  font-size: $font-size-base;
}

.lifecycle-list {
  list-style-type: none;
  padding-left: 0;
  margin: $space-2 0;

  .lifecycle-item {
    position: relative;
    padding-left: $space-5;
    margin-bottom: $space-2;
    font-size: $font-size-base;
    color: var(--text-primary);
    line-height: 1.6;

    &:before {
      content: "•";
      position: absolute;
      left: $space-2;
      color: var(--text-secondary);
    }
  }
}

.chart-container {
  background: var(--bg-primary);
  border-radius: $radius-sm;
  padding: $space-6 $space-3;
  margin-bottom: $space-8;
  box-shadow: $shadow-sm;
  height: rem(400);

  // Make specific charts wider
  &:nth-child(3),
  &:nth-child(4) {
    grid-column: span 2;
    padding: $space-6 $space-2;
  }

  .chart-title {
    margin: 0 0 $space-6;
    font-size: $font-size-lg;
    font-weight: 500;
    color: var(--text-primary);
  }

  .chart {
    width: 100%;
    height: calc(100% - #{$space-12});
    position: relative;
  }

}

// Visualization dialog styles
.visualization-dialog {
  .v-card__text {
    padding: $space-6 $space-3;

    .v-row {
      margin: 0 rem(-6);

      .v-col {
        padding: $space-3 rem(6);
      }
    }
  }
}

.raw-log-dialog-content-header {
  display: flex;
  align-items: center;
  gap: $space-4;
  padding: $space-4 $space-6 0;
  background-color: var(--bg-primary);

  .raw-log-dialog-search {
    min-width: 50%;
  }

  .has-keyword-toggle {
    margin-left: auto;
  }
}

// Remove background from the statistics viewer close button on hover
::v-deep .close-visualizations-btn {
  background: transparent !important;
  box-shadow: none !important;
  &::before {
    display: none !important;
  }
}
</style>

<style lang="scss">
/* Unscoped: D3-generated SVG elements don't have Vue scoped attributes */
.chart-container {
  svg {
    text {
      fill: var(--text-primary);
    }

    .domain,
    .tick line {
      stroke: var(--text-secondary);
    }

    /* Add border to legend color squares for visibility in dark mode */
    .legend rect {
      [data-theme="dark"] & {
        stroke: white;
        stroke-width: 1.5;
      }
    }
  }
}

// Raw Log Dialog — override Vuetify defaults for a clean white/themed surface
.raw-log-card {
  background-color: var(--bg-primary) !important;

  .raw-log-card__title {
    background-color: var(--bg-primary) !important;
    color: var(--text-primary) !important;
    padding: 16px 24px;
    font-size: 1.5rem !important;
    font-weight: 600;
  }

  // Remove the gray strip Vuetify adds to v-card-text
  .v-card__text {
    background-color: var(--bg-primary) !important;
    padding: 0 24px 16px !important;
  }

  // Tabs bar — no gray background
  .v-tabs-bar {
    background-color: var(--bg-primary) !important;
  }

  // Active/selected tab — text and icon use brand primary
  .v-tab--active {
    color: var(--brand-primary) !important;

    .v-icon {
      color: var(--brand-primary) !important;
    }
  }

  // Inactive tab — muted
  .v-tab:not(.v-tab--active) {
    color: var(--text-secondary) !important;

    .v-icon {
      color: var(--text-secondary) !important;
    }
  }

  // Active tab underline indicator
  .v-tabs-slider {
    background-color: var(--brand-primary) !important;
    color: var(--brand-primary) !important;
  }

  // Tab items container — no gray background
  .v-tabs-items,
  .v-window__container,
  .v-window-item {
    background-color: var(--bg-primary) !important;
  }

  // Data tables inside the dialog — flat, no shadow, themed background
  .raw-log-table {
    background-color: var(--bg-primary) !important;
    box-shadow: none !important;

    thead tr th {
      background-color: var(--bg-primary) !important;
      color: var(--text-secondary) !important;
      font-size: 0.75rem !important;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      border-bottom: 1px solid var(--border-secondary) !important;
    }

    tbody tr {
      background-color: var(--bg-primary) !important;

      &:hover {
        background-color: var(--bg-tertiary) !important;
      }

      td {
        color: var(--text-primary) !important;
        border-bottom: 1px solid var(--border-secondary) !important;
      }
    }

    // Footer
    .v-data-footer {
      background-color: var(--bg-primary) !important;
      border-top: 1px solid var(--border-secondary) !important;
      color: var(--text-secondary) !important;
    }
  }
}
</style>
